import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid2,
  Paper,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  PhotoLibrary as PhotoLibraryIcon,
  CropFree as CropFreeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import chroma from 'chroma-js';
import { HeroBannerEditableData, HeroBannerProps } from '../../../../types';

const HeroBanner: React.FC<HeroBannerProps> = ({
  userPhoto,
  workspaceId,
  previewMode,
  heroBannerEditableData,
  setHeroBannerEditableData,
  onSave,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showTags, setShowTags] = useState(
    heroBannerEditableData?.tags && heroBannerEditableData.tags.length > 0
  );
  const [showDescription, setShowDescription] = useState(
    heroBannerEditableData?.description &&
      heroBannerEditableData.description.trim().length > 0
  );
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [activatedDefaultTags, setActivatedDefaultTags] = useState<string[]>(
    []
  );

  useEffect(() => {
    if (heroBannerEditableData?.tags) {
      const existingTags = heroBannerEditableData.tags;
      const defaultTagsFromData = existingTags.filter((tag) =>
        defaultTags.includes(tag)
      );
      const customTagsFromData = existingTags.filter(
        (tag) => !defaultTags.includes(tag)
      );

      setActivatedDefaultTags(defaultTagsFromData);
      setCustomTags(customTagsFromData);
    }
  }, [heroBannerEditableData?.tags]);

  useEffect(() => {
    const hasTags =
      heroBannerEditableData?.tags && heroBannerEditableData.tags.length > 0;
    setShowTags(hasTags || previewMode);
  }, [heroBannerEditableData?.tags, previewMode]);

  useEffect(() => {
    const hasDescription =
      heroBannerEditableData?.description &&
      heroBannerEditableData.description.trim().length > 0;
    setShowDescription(hasDescription || previewMode);
  }, [heroBannerEditableData?.description, previewMode]);

  const syncTagsToEditableData = (
    newActivatedDefaultTags: string[],
    newCustomTags: string[]
  ) => {
    const allTags = [...newActivatedDefaultTags, ...newCustomTags];
    setHeroBannerEditableData?.({
      ...heroBannerEditableData,
      tags: allTags,
    } as HeroBannerEditableData);
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [backgroundDialogOpen, setBackgroundDialogOpen] = useState(false);
  const [newTag, setNewTag] = useState('');

  const defaultTags = [
    'professionalExperience',
    'skills',
    'training',
    'projects',
  ];

  const availableBackgrounds = [
    'background00',
    'background01',
    'background02',
    'background03',
    'background04',
    'background05',
    'background06',
    'background07',
    'background08',
    'default',
  ];

  const handleSave = () => {
    if (onSave && workspaceId) {
      onSave(workspaceId, heroBannerEditableData as HeroBannerEditableData);
    }
  };

  const handleAddCustomTag = () => {
    if (newTag.trim() && !customTags.includes(newTag.trim())) {
      const newCustomTags = [...customTags, newTag.trim()];
      setCustomTags(newCustomTags);
      syncTagsToEditableData(activatedDefaultTags, newCustomTags);
      setNewTag('');
      setDialogOpen(false);
    }
  };

  const handleRemoveCustomTag = (tagToRemove: string) => {
    const newCustomTags = customTags.filter((tag) => tag !== tagToRemove);
    setCustomTags(newCustomTags);
    syncTagsToEditableData(activatedDefaultTags, newCustomTags);
  };

  const handleToggleDefaultTag = (tag: string) => {
    let newActivatedDefaultTags: string[];
    if (activatedDefaultTags.includes(tag)) {
      newActivatedDefaultTags = activatedDefaultTags.filter((t) => t !== tag);
    } else {
      newActivatedDefaultTags = [...activatedDefaultTags, tag];
    }
    setActivatedDefaultTags(newActivatedDefaultTags);
    syncTagsToEditableData(newActivatedDefaultTags, customTags);
  };

  const handleToggleAvatarStyle = () => {
    if (setHeroBannerEditableData) {
      const currentStyle = heroBannerEditableData?.avatarStyle || 'square';
      const newStyle = currentStyle === 'round' ? 'square' : 'round';
      setHeroBannerEditableData({
        ...heroBannerEditableData,
        avatarStyle: newStyle,
      } as HeroBannerEditableData);
    }
  };

  const handleBackgroundSelect = (
    setHeroBannerEditableData: (data: HeroBannerEditableData) => void,
    backgroundName: string
  ) => {
    const updatedData = {
      ...heroBannerEditableData,
      selectedBackground: backgroundName,
    } as HeroBannerEditableData;

    setHeroBannerEditableData?.(updatedData);
    getTextColorFromImage(
      setHeroBannerEditableData,
      `/${backgroundName}.webp`,
      updatedData
    );
    setBackgroundDialogOpen(false);
  };

  const getTextColorBasedOnLuminance = (luminance: number): string => {
    if (luminance < 0.5) return '#FFFFFF';
    return '#000000';
  };

  const getTextColorFromImage = (
    setHeroBannerEditableData: (data: HeroBannerEditableData) => void,
    imageUrl: string,
    currentData?: HeroBannerEditableData
  ) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 10;
      canvas.height = 10;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.drawImage(img, 0, 0, 10, 10);
      const imageData = ctx.getImageData(0, 0, 10, 10).data;

      const pixels = [];
      for (let i = 0; i < imageData.length; i += 4) {
        const r = imageData[i];
        const g = imageData[i + 1];
        const b = imageData[i + 2];
        pixels.push([r, g, b]);
      }

      const avgColor = pixels
        .reduce(
          (acc, [r, g, b]) => [acc[0] + r, acc[1] + g, acc[2] + b],
          [0, 0, 0]
        )
        .map((c) => c / pixels.length);

      const luminance = chroma
        .rgb(avgColor[0], avgColor[1], avgColor[2])
        .luminance();
      const textColor = getTextColorBasedOnLuminance(luminance);

      // setTextColor(textColor);
      setHeroBannerEditableData?.({
        ...(currentData || heroBannerEditableData),
        bannerTextColor: textColor,
      } as HeroBannerEditableData);
    };
  };

  const textColor = heroBannerEditableData?.bannerTextColor || 'white';

  const renderTags = () => {
    if (previewMode) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: isMobile ? 0.5 : 1,
            mb: isMobile ? 1.5 : 2,
          }}
        >
          {defaultTags.map((tag, index) => {
            const isActivated = activatedDefaultTags.includes(tag);
            return (
              <Chip
                key={`default-${index}`}
                label={t(`personalizedPage.${tag}`)}
                size={isMobile ? 'small' : 'small'}
                onClick={() => handleToggleDefaultTag(tag)}
                sx={{
                  bgcolor: isActivated ? `${textColor}20` : `${textColor}05`,
                  color: isActivated ? textColor : `${textColor}80`,
                  border: isActivated
                    ? `1px solid ${textColor}30`
                    : `1px dashed ${textColor}30`,
                  opacity: isActivated ? 1 : 0.6,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: isMobile ? '0.65rem' : '0.75rem',
                  height: isMobile ? '24px' : '32px',
                  '&:hover': {
                    bgcolor: isActivated ? `${textColor}30` : `${textColor}10`,
                    opacity: 1,
                  },
                }}
              />
            );
          })}
          {customTags.map((tag, index) => (
            <Chip
              key={`custom-${index}`}
              label={tag}
              size={isMobile ? 'small' : 'small'}
              onDelete={() => handleRemoveCustomTag(tag)}
              sx={{
                bgcolor: `${textColor}20`,
                color: textColor,
                border: `1px solid ${textColor}30`,
                fontSize: isMobile ? '0.65rem' : '0.75rem',
                height: isMobile ? '24px' : '32px',
                '& .MuiChip-deleteIcon': {
                  color: `${textColor}70`,
                  fontSize: isMobile ? '16px' : '18px',
                },
              }}
            />
          ))}
          <Chip
            icon={<AddIcon fontSize={isMobile ? 'small' : 'small'} />}
            label={t('common.add')}
            size={isMobile ? 'small' : 'small'}
            onClick={() => setDialogOpen(true)}
            sx={{
              bgcolor: `${textColor}10`,
              color: textColor,
              border: `1px dashed ${textColor}50`,
              cursor: 'pointer',
              fontSize: isMobile ? '0.65rem' : '0.75rem',
              height: isMobile ? '24px' : '32px',
              '&:hover': {
                bgcolor: `${textColor}20`,
                color: textColor,
              },
            }}
          />
        </Box>
      );
    } else if (
      showTags &&
      (activatedDefaultTags.length > 0 || customTags.length > 0)
    ) {
      const allActiveTags = [
        ...activatedDefaultTags.map((tag) => t(`personalizedPage.${tag}`)),
        ...customTags,
      ];

      return (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: isMobile ? 0.5 : 1,
            mb: isMobile ? 1.5 : 2,
          }}
        >
          {allActiveTags.map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              size={isMobile ? 'small' : 'small'}
              sx={{
                backdropFilter: 'blur(3px)',
                bgcolor: 'rgba(255,255,255,0.2)',
                color: textColor,
                fontSize: isMobile ? '0.65rem' : '0.75rem',
                height: isMobile ? '24px' : '32px',
              }}
            />
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: isMobile ? 1 : 4,
        mt: isMobile ? 1 : 4,
        px: isMobile ? 0 : 3,
      }}
    >
      <Card
        elevation={isMobile ? 1 : 3}
        sx={{
          borderRadius: isMobile ? 2 : 5,
          overflow: 'hidden',
          position: 'relative',
          ...(heroBannerEditableData?.selectedBackground === 'default'
            ? {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              }
            : {
                backgroundImage: `url('/${heroBannerEditableData?.selectedBackground}.webp')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }),
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
          }}
        />
        {previewMode && (
          <>
            <IconButton
              onClick={() => setBackgroundDialogOpen(true)}
              sx={{
                position: 'absolute',
                top: isMobile ? 8 : 16,
                right: isMobile ? 8 : 16,
                zIndex: 2,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: textColor,
                size: isMobile ? 'small' : 'medium',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              <PhotoLibraryIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          </>
        )}

        <CardContent
          sx={{
            position: 'relative',
            color: textColor,
            p: isMobile ? 2 : 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: isMobile ? 2 : 3,
              flexDirection: isMobile ? 'column' : 'row',
              textAlign: isMobile ? 'center' : 'left',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: {
                  xs: isMobile ? '80px' : '30%',
                  sm: isMobile ? '100px' : '20%',
                  md: '15%',
                },
                height: {
                  xs: isMobile ? '80px' : '30%',
                  sm: isMobile ? '100px' : '80%',
                },
                mb: isMobile ? 2 : 0,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  bgcolor: 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 6,
                  ...(heroBannerEditableData?.avatarStyle === 'round'
                    ? {
                        aspectRatio: '1 / 1',
                        borderRadius: '50%',
                      }
                    : {
                        borderTopLeftRadius: isMobile ? 12 : 24,
                        borderTopRightRadius: isMobile ? 12 : 24,
                        borderBottomLeftRadius: isMobile ? 12 : 24,
                        borderBottomRightRadius: isMobile ? 12 : 24,
                      }),
                }}
              >
                <Box
                  component="img"
                  src={userPhoto || '/avatar.png'}
                  alt={'AI Assistant'}
                  sx={{
                    width: '100%',
                    height: '100%',
                    maxWidth: '200px',
                    maxHeight: '200px',
                    background: 'linear-gradient(135deg, #2196f3, #21cbf3)',
                    objectFit: 'cover',
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...(heroBannerEditableData?.avatarStyle === 'round'
                      ? {
                          aspectRatio: '1 / 1',
                          borderRadius: '50%',
                        }
                      : {
                          borderTopLeftRadius: isMobile ? 12 : 24,
                          borderTopRightRadius: isMobile ? 12 : 24,
                          borderBottomLeftRadius: isMobile ? 12 : 24,
                          borderBottomRightRadius: isMobile ? 12 : 24,
                        }),
                    boxShadow: 6,
                  }}
                />
              </Box>

              {/* Bouton de style d'avatar positionné en bas à droite */}
              {previewMode && (
                <IconButton
                  onClick={handleToggleAvatarStyle}
                  title={`${t('common.avatarStyle', 'Avatar Style')}: ${heroBannerEditableData?.avatarStyle === 'round' ? t('common.round', 'Round') : t('common.square', 'Square')}`}
                  sx={{
                    position: 'absolute',
                    bottom: -8,
                    right: -8,
                    zIndex: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    color: theme.palette.primary.main,
                    width: isMobile ? 28 : 32,
                    height: isMobile ? 28 : 32,
                    boxShadow: 2,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 1)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <CropFreeIcon fontSize={isMobile ? 'small' : 'medium'} />
                </IconButton>
              )}
            </Box>
            <Box
              sx={{
                ml: isMobile ? 0 : 2,
                flex: 1,
                mt: isMobile ? 0 : 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                width: isMobile ? '100%' : 'auto',
              }}
            >
              {previewMode ? (
                <TextField
                  value={heroBannerEditableData?.title || ''}
                  onChange={(e) =>
                    setHeroBannerEditableData?.({
                      ...heroBannerEditableData,
                      title: e.target.value,
                    } as HeroBannerEditableData)
                  }
                  variant="standard"
                  fullWidth
                  sx={{
                    mb: 1,
                    '&:hover': {
                      borderBottomColor: `${textColor}50`,
                    },
                    '& .MuiInput-root': {
                      color: textColor,
                      fontSize: isMobile ? '1.5rem' : '2.125rem',
                      fontWeight: 'bold',
                      '&:before': {
                        borderBottomColor: `${textColor}50`,
                      },
                      '&:after': {
                        borderBottomColor: `${textColor}50`,
                      },
                    },
                    '& .MuiInput-input': {
                      color: textColor,
                      fontWeight: 'bold',
                      textAlign: 'center',
                    },
                  }}
                />
              ) : (
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  fontWeight="bold"
                  gutterBottom
                  sx={{
                    textAlign: 'center',
                    fontSize: isMobile ? '1.5rem' : '2.125rem',
                  }}
                >
                  {heroBannerEditableData?.title}
                </Typography>
              )}
              {renderTags()}
              {showDescription &&
                (previewMode ? (
                  <TextField
                    value={heroBannerEditableData?.description || ''}
                    onChange={(e) =>
                      setHeroBannerEditableData?.({
                        ...heroBannerEditableData,
                        description: e.target.value,
                      } as HeroBannerEditableData)
                    }
                    variant="standard"
                    fullWidth
                    multiline
                    rows={isMobile ? 2 : 3}
                    sx={{
                      maxWidth: isMobile ? '100%' : '800px',
                      backdropFilter: 'blur(5px)',
                      backgroundColor:
                        textColor === 'white'
                          ? 'rgba(0, 0, 0, 0.5)'
                          : 'rgba(255, 255, 255, 0.6)',
                      color: textColor === 'white' ? 'white' : 'black',
                      '& .MuiInput-root': {
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        '&:before': {
                          borderBottomColor: `${textColor}50`,
                        },
                        '&:after': {
                          borderBottomColor: `${textColor}50`,
                        },
                      },
                      '& .MuiInput-input': {
                        color: textColor === 'white' ? 'white' : 'black',
                      },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      backdropFilter: 'blur(3px)',
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: textColor,
                      // backgroundColor:
                      //   textColor === 'white'
                      //     ? 'rgba(0, 0, 0, 0.5)'
                      //     : 'rgba(255, 255, 255, 0.6)',
                      // color: textColor === 'white' ? 'white' : 'black',
                      px: isMobile ? 1.5 : 2,
                      py: isMobile ? 0.75 : 1,
                      borderRadius: 2,
                      display: 'inline-block',
                      width: 'fit-content',
                      maxWidth: isMobile ? '100%' : '800px',
                    }}
                  >
                    <Typography
                      variant={isMobile ? 'body2' : 'body2'}
                      sx={{
                        opacity: 0.9,
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        lineHeight: isMobile ? 1.3 : 1.5,
                      }}
                    >
                      {heroBannerEditableData?.description}
                    </Typography>
                  </Box>
                ))}
              {previewMode && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: isMobile ? 8 : 16,
                    right: isMobile ? 8 : 16,
                    zIndex: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={
                      <SaveIcon fontSize={isMobile ? 'small' : 'medium'} />
                    }
                    onClick={handleSave}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      color: theme.palette.primary.main,
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 1)',
                      },
                    }}
                  >
                    {t('common.save', 'Save')}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth={isMobile ? 'sm' : 'md'}
        fullWidth
        PaperProps={{
          sx: {
            margin: isMobile ? 1 : 3,
            maxHeight: isMobile ? '90vh' : '80vh',
          },
        }}
      >
        <DialogTitle>{t('common.addTag', 'Add Tag')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('common.tagName', 'Tag Name')}
            fullWidth
            variant="outlined"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddCustomTag();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleAddCustomTag} variant="contained">
            {t('common.add', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={backgroundDialogOpen}
        onClose={() => setBackgroundDialogOpen(false)}
        maxWidth={isMobile ? 'sm' : 'md'}
        fullWidth
        PaperProps={{
          sx: {
            margin: isMobile ? 1 : 3,
            maxHeight: isMobile ? '90vh' : '80vh',
          },
        }}
      >
        <DialogTitle>
          {t('common.selectBackground', 'Select Background')}
        </DialogTitle>
        <DialogContent>
          <Grid2 container spacing={isMobile ? 1 : 2} sx={{ mt: 1 }}>
            {availableBackgrounds.map((bg, index) => (
              <Grid2
                size={{ xs: isSmallMobile ? 12 : 6, sm: 4, md: 3 }}
                key={bg}
              >
                <Paper
                  sx={{
                    position: 'relative',
                    paddingTop: '60%', // Ratio 5:3
                    cursor: 'pointer',
                    border:
                      heroBannerEditableData?.selectedBackground === bg
                        ? `3px solid ${theme.palette.primary.main}`
                        : '1px solid #ddd',
                    borderRadius: isMobile ? 1 : 2,
                    overflow: 'hidden',
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                    },
                  }}
                  onClick={() =>
                    setHeroBannerEditableData &&
                    handleBackgroundSelect(setHeroBannerEditableData, bg)
                  }
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `url('/${bg}.webp')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                  {heroBannerEditableData?.selectedBackground === bg && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: isMobile ? 4 : 8,
                        right: isMobile ? 4 : 8,
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        borderRadius: '50%',
                        width: isMobile ? 20 : 24,
                        height: isMobile ? 20 : 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: isMobile ? '10px' : '12px',
                      }}
                    >
                      ✓
                    </Box>
                  )}
                </Paper>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 1,
                    fontSize: isMobile ? '0.65rem' : '0.75rem',
                    color:
                      heroBannerEditableData?.selectedBackground === bg
                        ? theme.palette.primary.main
                        : 'text.secondary',
                  }}
                >
                  {bg}
                </Typography>
              </Grid2>
            ))}
          </Grid2>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackgroundDialogOpen(false)}>
            {t('common.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HeroBanner;
